import type {
  InterstateOnChangeEvent,
  MenuItemEvent
} from "@dealer-center/core-components";
import {
  ChevronDownIcon,
  MenuButton,
  MuiIconButton,
  Typography,
  useMenu
} from "@dealer-center/core-components";
import { useNavBarStore } from "../../store";
import { backgroundColor } from "../../const";

export interface AccountSelectorProps {
  iconOnly?: boolean;
}

export const AccountSelector = ({ iconOnly }: AccountSelectorProps) => {
  const {
    selectedAccountName: selectedAccount,
    availableAccounts,
    setSelectedAccountId: selectAccount,
    selectedAccountStatus
  } = useNavBarStore();

  const menuReferenceProps = useMenu();
  const isSingleAccount = !availableAccounts || availableAccounts.length === 0;
  const isNothingToDo = !selectedAccount && isSingleAccount;
  if (isNothingToDo) return null;

  const resolvedAccountName =
    availableAccounts?.find(account => account.value === selectedAccount)
      ?.label || selectedAccount;
  const resolvedAccountNameWithStatus = selectedAccountStatus
    ? `${resolvedAccountName} (${selectedAccountStatus})`
    : resolvedAccountName;
  const onClick = (
    event:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (isSingleAccount || !menuReferenceProps.onClick) return;
    menuReferenceProps.onClick(event);
  };

  if (iconOnly) {
    return (
      <MuiIconButton
        aria-haspopup="true"
        aria-label="Account Selector"
        color="inherit"
        id="account-selector"
        size="large"
        onClick={onClick}
      >
        <ChevronDownIcon title="Account Selector" />
      </MuiIconButton>
    );
  }

  return (
    <MenuButton
      ButtonProps={{
        id: "account-selector",
        sx: {
          // hover
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)"
          },
          backgroundColor,
          borderColor: "rgba(255, 255, 255, 0.2)",
          borderWidth: 1,
          borderStyle: "solid"
        }
      }}
      MenuProps={{
        id: "account-selector-menu",
        menuOptions:
          availableAccounts?.map(account => ({
            value: account.value,
            onSelect: (event: InterstateOnChangeEvent<MenuItemEvent>) => {
              const selectedValue = event.target.value?.toString();
              if (selectAccount && selectedValue) selectAccount(selectedValue);
            },
            label:
              selectedAccount === account.value
                ? `✓ ${account.label} (${account.value})`
                : `${account.label} (${account.value})`,
            disabled: selectedAccount === account.value
          })) || []
      }}
      aria-haspopup="true"
      aria-label="Account Selector"
    >
      <Typography
        sx={{
          cursor: !isSingleAccount ? "pointer" : "default",
          color: "white",
          display: "flex",
          fontWeight: "normal",
          alignItems: "center",
          "&>svg": {
            ml: 1
          }
        }}
        variant="h3"
      >
        {resolvedAccountNameWithStatus || "Recent Accounts"}
        {!isSingleAccount && <ChevronDownIcon title="Account Selector" />}
      </Typography>
    </MenuButton>
  );
};
